/**
 * Makes an infinite 3D slider genuinely endless: there is always a previous and
 * a next card on show, and the join between the last slide and the first is
 * invisible in both directions.
 *
 * It works the way carousels traditionally do, by padding each end of the strip
 * with clones of the opposite end. Stepping off the last slide scrolls one card
 * forward onto the clone of the first, and once that scroll has finished the
 * scroll position is moved back to the real first slide. Because the clone and
 * the slide it copies are identical, and enough clones are made that their
 * neighbours match too, the viewer sees one continuous card-width scroll.
 *
 * The clones are presentation only. They deliberately carry no `ref` attribute,
 * so slideshow.js never collects them into refs.slides: the slide count, the
 * dots, the current index and the scroll maths all continue to see exactly the
 * real slides and nothing else.
 */

const CLONE_CLASS = 'slideshow-3d__edge-clone';

/**
 * How many slides to copy onto each end. Two is enough for a layout that shows
 * one neighbour either side: when the clone of the first slide is centred, the
 * clone of the second has to be sitting next to it for the view to match the
 * real first slide's.
 */
const CLONE_DEPTH = 2;

/** Fallback in case the browser does not fire scrollend. */
const SCROLL_TIMEOUT = 1000;

/** Minimum horizontal travel, in px, before a pointer gesture counts as a swipe. */
const DRAG_THRESHOLD = 40;

class SlideshowLoopEdges extends HTMLElement {
  #built = false;
  #wrapping = false;
  #endsWired = false;
  #dragWired = false;

  connectedCallback() {
    // The clones are built from the upgraded slideshow's own slides, so wait
    // for custom elements to be defined before reading them.
    customElements.whenDefined('slideshow-component').then(() => {
      requestAnimationFrame(() => this.#build());
    });
  }

  disconnectedCallback() {
    for (const clone of this.querySelectorAll(`.${CLONE_CLASS}`)) clone.remove();
    this.#built = false;
  }

  /**
   * Rebuilds clones and card wrappers after slide contents change.
   * Used when reviews are hydrated into an already-mounted slider.
   */
  rebuild() {
    for (const clone of this.querySelectorAll(`.${CLONE_CLASS}`)) clone.remove();
    this.#built = false;
    this.#wrapTargets = null;
    this.#build();
  }

  #build() {
    if (this.#built || !this.isConnected) return;

    const slideshow = /** @type {any} */ (this.querySelector('slideshow-component'));
    const scroller = /** @type {HTMLElement | null} */ (slideshow?.querySelector('slideshow-slides'));
    if (!slideshow || !scroller) return;

    // Only endless sliders need padding out. A finite one should show empty
    // space at the ends, because that is where it stops.
    if (!slideshow.hasAttribute('infinite')) return;

    const slides = /** @type {HTMLElement[]} */ ([...scroller.querySelectorAll(':scope > slideshow-slide[ref]')]);
    if (slides.length < 2) return;

    // The inner card exists solely as the 3D transform target. Standard
    // sliders can use the same seamless edge clones without changing their
    // native Shopify slide markup.
    if (slideshow.classList.contains('slideshow-3d')) {
      for (const slide of slides) this.#wrapCard(slide);
    }

    const requestedDepth = Number(this.dataset.cloneDepth);
    const cloneDepth = Number.isFinite(requestedDepth) && requestedDepth > 0 ? requestedDepth : CLONE_DEPTH;
    const depth = Math.min(cloneDepth, slides.length);
    const first = slides[0];
    const last = slides[slides.length - 1];

    // Tail of the strip copied in front of the head, and vice versa, each kept
    // in its original order so the sequence reads continuously across the join.
    const leading = slides.slice(slides.length - depth).map((slide, i) => this.#cloneOf(slide, `leading-${i}`));
    const trailing = slides.slice(0, depth).map((slide, i) => this.#cloneOf(slide, `trailing-${i}`));

    first.before(...leading);
    last.after(...trailing);

    // The clone standing in for each end: the one immediately beside the real
    // strip, which is what a single step off either end lands on.
    this.#wrapTargets = {
      forward: { clone: trailing[0], slide: first, index: 0 },
      backward: { clone: leading[leading.length - 1], slide: last, index: slides.length - 1 },
    };

    this.#built = true;
    this.#interceptEnds(slideshow, scroller);
    this.#interceptDrag(slideshow, scroller);

    // Inserting slides ahead of the strip shifts every real slide along, so
    // re-anchor on whichever slide was showing before the insert.
    this.#jumpTo(scroller, slides[slideshow.current] ?? first);
  }

  /** @type {{forward: {clone: HTMLElement, slide: HTMLElement, index: number}, backward: {clone: HTMLElement, slide: HTMLElement, index: number}} | null} */
  #wrapTargets = null;

  /**
   * Takes over next() and previous() at the two ends of the strip.
   *
   * Left to itself, slideshow.js loops by relocating the slide it is travelling
   * to so the scroll stays one card long. That lands the incoming slide right
   * beside this element's clone of it, showing the same card twice. Handling
   * the ends here means the clone is scrolled to rather than scrolled past, so
   * nothing is duplicated and nothing has to be hidden to cover it up.
   *
   * Both arrows and autoplay call these methods, so overriding them on the
   * instance covers every way of reaching the end.
   *
   * @param {any} slideshow
   * @param {HTMLElement} scroller
   * @param {number} slideCount
   */
  #interceptEnds(slideshow, scroller) {
    if (this.#endsWired) return;
    this.#endsWired = true;

    const lastIndex = () => scroller.querySelectorAll(':scope > slideshow-slide[ref]').length - 1;
    const { next, previous } = slideshow;

    slideshow.next = (/** @type {any} */ event, /** @type {any} */ options) => {
      if (slideshow.current === lastIndex()) return this.#wrap(slideshow, scroller, 'forward', event);
      return next.call(slideshow, event, options);
    };

    slideshow.previous = (/** @type {any} */ event, /** @type {any} */ options) => {
      if (slideshow.current === 0) return this.#wrap(slideshow, scroller, 'backward', event);
      return previous.call(slideshow, event, options);
    };
  }

  /**
   * Makes a swipe loop the same way the arrows do.
   *
   * The theme's own drag handler snaps to the nearest real slide and clamps the
   * result to the two ends, so a swipe off the last slide scrolls partway onto
   * the clone and then springs back. That spring-back is what reads as the image
   * changing. Rather than fight that handler, this waits for it to finish and,
   * if the swipe was an attempt to move past an end, runs the same wrap the
   * arrows use.
   *
   * The intent test is deliberately narrow: the drag has to have *started* at
   * the end it is leaving. A swipe that merely lands on the last slide from the
   * one before it started elsewhere, so it is left alone.
   *
   * @param {any} slideshow
   * @param {HTMLElement} scroller
   * @param {number} slideCount
   */
  #interceptDrag(slideshow, scroller) {
    if (this.#dragWired) return;
    this.#dragWired = true;

    const lastIndex = () => scroller.querySelectorAll(':scope > slideshow-slide[ref]').length - 1;
    let startIndex = 0;
    let startX = 0;
    let active = false;

    scroller.addEventListener(
      'pointerdown',
      (event) => {
        startIndex = slideshow.current;
        startX = event.clientX;
        active = true;
      },
      true
    );

    // Bound on window so it is heard even while the component holds the pointer
    // capture, and after the theme's own pointerup has updated the index. The
    // active flag makes sure a release only counts for the slider that the
    // gesture began on, so a second slider on the page never reacts to it.
    window.addEventListener('pointerup', (event) => {
      if (!active) return;
      active = false;
      if (this.#wrapping || !this.#wrapTargets) return;

      const dx = event.clientX - startX;
      if (Math.abs(dx) < DRAG_THRESHOLD) return; // a tap or a click, not a swipe

      const forward = dx < 0;

      // Let the theme settle its own snap-back before checking where it landed.
      requestAnimationFrame(() => {
        if (this.#wrapping) return;
        if (forward && startIndex === lastIndex() && slideshow.current === lastIndex()) {
          this.#wrap(slideshow, scroller, 'forward');
        } else if (!forward && startIndex === 0 && slideshow.current === 0) {
          this.#wrap(slideshow, scroller, 'backward');
        }
      });
    });
  }

  /**
   * Scrolls one card onto the clone at the end being left, then moves the
   * scroll position across to the matching real slide once it has settled.
   *
   * @param {any} slideshow
   * @param {HTMLElement} scroller
   * @param {'forward' | 'backward'} direction
   * @param {Event} [event]
   */
  #wrap(slideshow, scroller, direction, event) {
    event?.preventDefault();
    if (this.#wrapping || !this.#wrapTargets) return;

    const { clone, slide, index } = this.#wrapTargets[direction];
    this.#wrapping = true;

    scroller.scrollTo({ left: this.#positionOf(scroller, clone), behavior: 'smooth' });

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      scroller.removeEventListener('scrollend', finish);

      // select() with animate: false lands on exactly the position the
      // slideshow would have used, and brings the index, the dots and the
      // arrow states along with it.
      slideshow.select(index, undefined, { animate: false });
      this.#wrapping = false;
    };

    const timer = setTimeout(finish, SCROLL_TIMEOUT);
    scroller.addEventListener('scrollend', finish);

    // A slide the browser is already sitting on fires no scroll at all.
    if (Math.abs(scroller.scrollLeft - this.#positionOf(scroller, clone)) < 1) finish();

    return undefined;
  }

  /**
   * The scroll position that puts an element where the slideshow would put it.
   * Mirrors the offset maths in scrolling.js so that a wrap ends on exactly the
   * position select() would have produced, leaving nothing to correct.
   *
   * @param {HTMLElement} scroller
   * @param {HTMLElement} el
   */
  #positionOf(scroller, el) {
    const paddingStart = parseFloat(getComputedStyle(scroller).paddingInlineStart) || 0;
    return el.offsetLeft - paddingStart;
  }

  /**
   * @param {HTMLElement} scroller
   * @param {HTMLElement} el
   */
  #jumpTo(scroller, el) {
    scroller.scrollTo({ left: this.#positionOf(scroller, el), behavior: 'instant' });
  }

  /**
   * Moves a slide's contents into a single wrapper that the depth animation can
   * transform.
   *
   * The depth effect cannot be applied to the slideshow-slide element itself.
   * slideshow.js measures slides to decide which one is centred and how far to
   * scroll, and a transformed element reports its painted box rather than its
   * layout box, so a scaled slide throws that maths out and the slider drifts
   * out of step and stalls before it can loop.
   *
   * A slide is also the card: base.css gives it the corner radius and the
   * clipping, and the colour scheme paints its background. Scaling only the
   * contents would leave those full size, so every card's background would stay
   * edge to edge and the cards would read as one continuous band. Moving the
   * card's own appearance onto the wrapper keeps the slide a plain, measurable
   * box while the whole card, background included, scales as one.
   *
   * @param {HTMLElement} slide
   */
  #wrapCard(slide) {
    if (slide.querySelector(':scope > .slideshow-3d__card')) return;

    // Read the background before it is cleared below, so the wrapper can carry
    // whatever the slide's colour scheme resolved to.
    const { backgroundColor, backgroundImage } = getComputedStyle(slide);

    const card = document.createElement('div');
    card.className = 'slideshow-3d__card';
    card.style.setProperty('--slideshow-3d-card-bg', backgroundColor);
    if (backgroundImage && backgroundImage !== 'none') {
      card.style.setProperty('--slideshow-3d-card-bg-image', backgroundImage);
    }

    while (slide.firstChild) card.appendChild(slide.firstChild);
    slide.appendChild(card);

    slide.style.background = 'none';
  }

  /**
   * @param {HTMLElement} slide - the real slide to copy
   * @param {string} name - unique suffix for this clone's timeline
   * @returns {HTMLElement}
   */
  #cloneOf(slide, name) {
    const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));

    clone.classList.add(CLONE_CLASS);
    clone.style.pointerEvents = 'none';
    clone.removeAttribute('ref');
    clone.removeAttribute('slide-id');
    clone.removeAttribute('hidden');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');

    // Strip declarative event bindings so a click on a clone cannot select a
    // slide by an index that no longer refers to it.
    for (const el of [clone, ...clone.querySelectorAll('*')]) {
      for (const { name: attr } of [...el.attributes]) {
        if (attr.startsWith('on:')) el.removeAttribute(attr);
      }
      el.removeAttribute('id');
    }

    // Clones sit off screen until the loop reaches them. Left lazy, their
    // images would only start downloading at the moment they scroll into view,
    // which is exactly the join the loop is meant to hide.
    for (const img of clone.querySelectorAll('img')) {
      img.setAttribute('loading', 'eager');
      img.removeAttribute('fetchpriority');
    }

    // base.css derives each slide's view timeline from --slideshow-timeline. Two
    // elements declaring the same timeline name in one scope makes that name
    // ambiguous, which would break the depth animation on the slide it was
    // cloned from, so give the clone a name of its own.
    clone.style.setProperty('--slideshow-timeline', `--slide-clone-${name}`);

    return clone;
  }
}

if (!customElements.get('slideshow-loop-edges')) {
  customElements.define('slideshow-loop-edges', SlideshowLoopEdges);
}
